import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

class PrimebaseSelectOripa extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Set<string>} */
  #selected = new Set();

  #activeRange = 'all';

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.querySelectorAll('[data-variant-id]').forEach((button) => {
      button.addEventListener('click', this.#onNumberClick, { signal });
    });
    this.querySelectorAll('[data-range-filter]').forEach((button) => {
      button.addEventListener('click', this.#onRangeClick, { signal });
    });
    this.querySelector('[data-pb-select-search]')?.addEventListener('input', this.#applyFilters, { signal });
    this.querySelector('[data-pb-select-add]')?.addEventListener('click', this.#addSelectedToCart, { signal });

    this.#updateSummary();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** @returns {HTMLButtonElement[]} */
  #numberButtons() {
    return Array.from(this.querySelectorAll('[data-variant-id]')).filter(
      (element) => element instanceof HTMLButtonElement
    );
  }

  /** @param {Event} event */
  #onNumberClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.#clearMessage();
    const mode = this.dataset.mode || 'single';

    if (mode === 'single') {
      this.#selected.clear();
      this.#selected.add(variantId);
    } else if (this.#selected.has(variantId)) {
      this.#selected.delete(variantId);
    } else if (mode === 'limited' && this.#selected.size >= this.#maxUnits()) {
      this.#showMessage(this.#limitMessage(), 'error');
      return;
    } else {
      this.#selected.add(variantId);
    }

    this.#updateSummary();
  };

  /** @param {Event} event */
  #onRangeClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    this.#activeRange = button.dataset.rangeFilter || 'all';
    this.querySelectorAll('[data-range-filter]').forEach((filter) => {
      const active = filter === button;
      filter.classList.toggle('is-active', active);
      filter.setAttribute('aria-pressed', String(active));
    });
    this.#applyFilters();
  };

  #applyFilters = () => {
    const searchInput = this.querySelector('[data-pb-select-search]');
    const query = searchInput instanceof HTMLInputElement ? this.#normalizeSearch(searchInput.value) : '';
    let visibleCount = 0;

    this.#numberButtons().forEach((button) => {
      const rangeMatches = this.#activeRange === 'all' || button.dataset.rangeIndex === this.#activeRange;
      const candidate = this.#normalizeSearch(button.dataset.searchValue || '');
      const searchMatches = query === '' || candidate.includes(query);
      const visible = rangeMatches && searchMatches;
      button.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    const empty = this.querySelector('[data-pb-select-empty]');
    if (empty instanceof HTMLElement) empty.hidden = visibleCount > 0;
  };

  /** @param {string} value */
  #normalizeSearch(value) {
    const normalized = value.trim().toLocaleLowerCase();
    if (/^\d+$/.test(normalized)) return String(Number(normalized));
    return normalized;
  }

  #maxUnits() {
    const max = Number(this.dataset.maxUnits);
    return Number.isInteger(max) && max > 0 ? max : 1;
  }

  #limitMessage() {
    return (this.dataset.limitMessage || '最大{max}口まで選択できます。').replace('{max}', String(this.#maxUnits()));
  }

  #updateSummary() {
    const selectedButtons = this.#numberButtons().filter((button) => {
      const selected = Boolean(button.dataset.variantId && this.#selected.has(button.dataset.variantId));
      button.setAttribute('aria-pressed', String(selected));
      return selected;
    });

    const selectedLabel = this.dataset.selectedLabel || '選択中';
    const selection = this.querySelector('[data-pb-select-selection]');
    if (selection instanceof HTMLElement) {
      if (selectedButtons.length === 0) {
        selection.textContent = `${selectedLabel}：未選択`;
      } else {
        const titles = selectedButtons.map((button) => `No.${button.dataset.variantTitle || ''}`).join('、');
        const prefix = selectedButtons.length === 1 ? selectedLabel : `${selectedButtons.length}口${selectedLabel}`;
        selection.textContent = `${prefix}：${titles}`;
      }
    }

    const currency = this.dataset.currency || '';
    const total = selectedButtons.reduce((sum, button) => {
      const price = convertMoneyToMinorUnits(button.dataset.variantPrice || '', currency);
      return sum + (price ?? 0);
    }, 0);
    const totalElement = this.querySelector('[data-pb-select-total]');
    if (totalElement instanceof HTMLElement) {
      totalElement.textContent = formatMoney(total, this.dataset.moneyFormat || '{{amount}}', currency);
    }

    const addButton = this.querySelector('[data-pb-select-add]');
    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled = selectedButtons.length === 0;
      const label = this.dataset.cartButtonText || '選択した番号をカートに追加';
      addButton.textContent = selectedButtons.length > 0 ? `${label}（${selectedButtons.length}口）` : label;
    }
  }

  #addSelectedToCart = async () => {
    const addButton = this.querySelector('[data-pb-select-add]');
    if (!(addButton instanceof HTMLButtonElement) || this.#selected.size === 0) return;

    const selectedIds = this.#numberButtons()
      .map((button) => button.dataset.variantId)
      .filter((id) => id && this.#selected.has(id));
    if (selectedIds.length === 0) return;

    addButton.disabled = true;
    addButton.setAttribute('aria-busy', 'true');
    this.#clearMessage();

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    const lines = selectedIds.map((variantId) => ({ merchandiseId: variantId, quantity: 1 }));

    this.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: deferred.promise })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: selectedIds.map((id) => ({ id: Number(id), quantity: 1 })),
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        await this.#handleCartFailure(result, deferred, selectedIds.length);
        return;
      }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'primebase-select-oripa',
          sourceId: this.id,
          itemCount: selectedIds.length,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
      this.#selected.clear();
      this.#updateSummary();
      this.#showMessage(this.dataset.cartSuccessMessage || 'カートに追加しました。', 'success');
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Add to cart failed',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      await this.#refreshAvailability();
      this.#showMessage(this.dataset.cartErrorMessage || 'カートに追加できませんでした。', 'error');
    } finally {
      addButton.removeAttribute('aria-busy');
      this.#updateSummary();
    }
  };

  /**
   * @param {Record<string, unknown>} result
   * @param {{resolve: Function, reject: Function}} deferred
   * @param {number} itemCount
   */
  async #handleCartFailure(result, deferred, itemCount) {
    this.dispatchEvent(
      new CartErrorEvent({
        error: typeof result.message === 'string' ? result.message : 'Add to cart failed',
        code: 'INVALID',
        detail: { description: result.description, errors: result.errors },
      })
    );

    const [cart] = await Promise.all([this.#fetchCart(), this.#refreshAvailability()]);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: true,
        items: cart.items,
        source: 'primebase-select-oripa',
        sourceId: this.id,
        itemCount,
        productId: this.dataset.productId,
      },
    });
    this.#showMessage(this.dataset.cartErrorMessage || '在庫状況が変わりました。番号を選び直してください。', 'error');
  }

  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems && 'fetchCartData' in cartItems && typeof cartItems.fetchCartData === 'function') {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  async #refreshAvailability() {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;

    try {
      const url = new URL(productUrl, window.location.origin);
      url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
      url.search = '';
      const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) return;
      const product = await response.json();
      const variants = new Map(product.variants.map((variant) => [String(variant.id), variant]));

      this.#numberButtons().forEach((button) => {
        const id = button.dataset.variantId;
        const variant = id ? variants.get(id) : null;
        if (!variant) return;

        const available = Boolean(variant.available);
        button.disabled = !available;
        button.classList.toggle('is-sold', !available);
        const sold = button.querySelector('.pb-select-oripa__sold');
        if (sold instanceof HTMLElement) sold.hidden = available;
        button.setAttribute(
          'aria-label',
          `番号 ${button.dataset.variantTitle || ''}${available ? '' : `、${this.dataset.soldText || 'SOLD'}`}`
        );
        if (!available && id) this.#selected.delete(id);
      });
      this.#updateSummary();
    } catch (_error) {
      // The cart error remains visible even when the availability refresh cannot complete.
    }
  }

  /** @param {string} message @param {'success' | 'error'} type */
  #showMessage(message, type) {
    const element = this.querySelector('[data-pb-select-message]');
    if (!(element instanceof HTMLElement)) return;
    element.textContent = message;
    element.dataset.type = type;
    element.hidden = false;
  }

  #clearMessage() {
    const element = this.querySelector('[data-pb-select-message]');
    if (!(element instanceof HTMLElement)) return;
    element.textContent = '';
    element.removeAttribute('data-type');
    element.hidden = true;
  }
}

if (!customElements.get('primebase-select-oripa')) {
  customElements.define('primebase-select-oripa', PrimebaseSelectOripa);
}
